import { useEffect, useState } from 'react'
import { AlignLeft, CalendarDays, MapPin, RefreshCw, ShieldCheck } from 'lucide-react'
import { useSaveBar } from '../shell/editing.js'
import { chargerSalles, modifierSoiree, type CorpsSoiree } from './soirees-maison-api.js'
import {
  HEURE_RX, VERROU_LIBELLE,
  etatBouton, heureDeNuitLisible, jourCivil, nomVide, nuitEnLettres, raisonNuitFigee,
  type ErreurBM, type Salle, type Soiree,
} from './soirees-maison-model.js'
import { dureeMinutes } from './assistant-soiree-model.js'
import { illustrationSalle } from './salles-illustrations.js'
import type { Ecrivain } from './SoireeMaisonPage.js'

/**
 * L'ONGLET « LA SOIRÉE » — nom, nuit, horaires, salles, jauge, famille,
 * présentation. UNE unité d'édition, la Save Bar ; le corps porte la `version`
 * lue (§3.6) et TOUS les champs (§4.6). Les heures non touchées repartent
 * telles qu'elles ont été lues (`ouverture_heure`) : la seconde occurrence de
 * la nuit du 25/10 reste la seconde.
 *
 * Ce qui ne se change plus (`verrous`) reste affiché, grisé, avec sa raison.
 * Une soirée programmée ne change pas de nuit (décision du 22/10) : il faut la
 * remettre en brouillon d'abord.
 */

interface SaisieSoiree {
  nom: string; nuit: string; ouverture: string; fermeture: string; jauge_totale: string
  salles: { code: string; nom: string; retenue: boolean; jauge: string }[]
  famille: string; presentation: string
}

function saisieDe(s: Soiree, catalogue: readonly Salle[]): SaisieSoiree {
  const codes = new Set(s.salles.map((x) => x.code))
  const toutes = [
    ...s.salles.map((x) => ({ code: x.code, nom: x.nom })),
    ...catalogue.filter((c) => c.active && !codes.has(c.code)).map((c) => ({ code: c.code, nom: c.nom })),
  ]
  return {
    nom: s.nom, nuit: s.nuit, ouverture: s.ouverture_heure ?? '', fermeture: s.fermeture_heure ?? '',
    jauge_totale: String(s.jauge_totale),
    salles: toutes.map((x) => {
      const lue = s.salles.find((y) => y.code === x.code)
      return { code: x.code, nom: x.nom, retenue: !!lue, jauge: lue?.jauge === null || lue?.jauge === undefined ? '' : String(lue.jauge) }
    }),
    famille: s.famille ?? '', presentation: s.presentation ?? '',
  }
}

function egal(a: SaisieSoiree, b: SaisieSoiree): number {
  let n = 0
  for (const k of ['nom', 'nuit', 'ouverture', 'fermeture', 'jauge_totale', 'famille', 'presentation'] as const) if (a[k] !== b[k]) n++
  const sal = (x: SaisieSoiree) => JSON.stringify(x.salles.filter((y) => y.retenue).map((y) => [y.code, y.jauge.trim()]))
  if (sal(a) !== sal(b)) n++
  return n
}

function corpsDe(v: SaisieSoiree, version: number): { corps: CorpsSoiree } | { erreur: string } {
  const nom = v.nom.trim()
  if (!nom || nom.length > 120) return { erreur: 'Le nom de la soirée est obligatoire, 120 caractères au plus.' }
  if (nomVide(nom)) return { erreur: 'Le nom de la soirée ne peut pas être fait seulement d’espaces ou de caractères invisibles.' }
  if (!jourCivil(v.nuit)) return { erreur: 'Choisissez la nuit de la soirée.' }
  if (!HEURE_RX.test(v.ouverture) || !HEURE_RX.test(v.fermeture)) return { erreur: 'Heures d’ouverture et de fermeture au format HH:MM.' }
  const d = dureeMinutes(v.ouverture, v.fermeture)
  if (d !== null && d > 16 * 60) return { erreur: 'Une soirée dure 16 h au plus.' }
  const jauge = Number(v.jauge_totale.trim())
  if (!/^\d{1,5}$/.test(v.jauge_totale.trim()) || jauge < 1 || jauge > 20_000) return { erreur: 'La jauge totale va de 1 à 20 000 personnes.' }
  const salles = v.salles.filter((s) => s.retenue)
  if (salles.length === 0) return { erreur: 'Ouvrez au moins une salle.' }
  for (const s of salles) {
    const j = s.jauge.trim()
    if (j && (!/^\d{1,5}$/.test(j) || Number(j) < 1 || Number(j) > 20_000)) return { erreur: `Jauge de ${s.nom} : de 1 à 20 000, ou vide.` }
  }
  if (v.famille.trim().length > 60) return { erreur: 'La famille tient en 60 caractères.' }
  if (v.presentation.trim().length > 4000) return { erreur: 'La présentation tient en 4 000 caractères.' }
  return {
    corps: {
      version, nom, nuit: v.nuit, ouverture: v.ouverture, fermeture: v.fermeture, jauge_totale: jauge,
      salles: salles.map((s) => ({ code: s.code, jauge: s.jauge.trim() ? Number(s.jauge.trim()) : null })),
      famille: v.famille.trim() || null, presentation: v.presentation.trim() || null,
    },
  }
}

/**
 * L'aide sous une heure : ce que l'heure saisie ne dit pas déjà (la saison
 * dans l'heure qui existe deux fois, l'heure qui n'existe pas), puis la règle.
 * Répéter « 23:55 » sous « 23:55 » n'apprenait rien (recette du 23/10/2026).
 */
const majuscule = (t: string): string => t.charAt(0).toUpperCase() + t.slice(1)

/**
 * La phrase sous les horaires, comme « Votre évènement durera 6 heures, 5
 * minutes » chez Shotgun : la nuit en lettres, puis la durée.
 */
function phraseDeLaNuit(v: SaisieSoiree): string {
  const nuit = jourCivil(v.nuit) ? `${majuscule(nuitEnLettres(v.nuit))}.` : 'Le jour où la soirée commence.'
  const d = dureeMinutes(v.ouverture, v.fermeture)
  if (d === null) return nuit
  const h = Math.floor(d / 60); const m = d % 60
  const duree = `${h} heure${h > 1 ? 's' : ''}${m ? `, ${m} minute${m > 1 ? 's' : ''}` : ''}`
  return `${nuit} La soirée durera ${duree} (avant 8 h, c’est le lendemain).`
}

function aideHeure(nuit: string, hhmm: string, regle: string | null) {
  const lue = HEURE_RX.test(hhmm) ? heureDeNuitLisible(nuit, hhmm) : null
  const texte = [lue && lue !== hhmm ? lue : HEURE_RX.test(hhmm) ? null : 'HH:MM', regle].filter(Boolean).join(' · ')
  return texte ? <span className="lc-helper">{texte}</span> : null
}

export function FicheSoireeForm({ ecrivain, onModifie }: { ecrivain: Ecrivain; onModifie: (m: boolean) => void }) {
  const { soiree: s, fiche } = ecrivain
  const [catalogue, setCatalogue] = useState<Salle[]>([])
  useEffect(() => { void chargerSalles().then((r) => { if (r.ok) setCatalogue(r.data) }) }, [])
  const [lu, setLu] = useState<SaisieSoiree>(() => saisieDe(s, []))
  const [v, setV] = useState<SaisieSoiree>(lu)
  const [version, setVersion] = useState(s.version)
  const [erreur, setErreur] = useState<ErreurBM | null>(null)

  const g = s.gestes.modifier
  const bouton = etatBouton('modifier_soiree', g, fiche.actions, fiche.ecriture)
  const n = egal(lu, v)
  const modifie = bouton.actif && n > 0
  useEffect(() => { onModifie(modifie) }, [modifie]) // eslint-disable-line react-hooks/exhaustive-deps

  // Une relecture (rafraîchissement, autre geste) remet la soirée à jour — sauf pendant une saisie.
  useEffect(() => {
    if (modifie) return
    const neuf = saisieDe(s, catalogue)
    setLu(neuf); setV(neuf); setVersion(s.version); setErreur(null)
  }, [s.version, catalogue]) // eslint-disable-line react-hooks/exhaustive-deps

  const corps = corpsDe(v, version)
  useSaveBar({
    dirty: modifie,
    count: n,
    saveLabel: 'Enregistrer la soirée',
    successMessage: 'Soirée enregistrée.',
    invalid: 'erreur' in corps ? corps.erreur : null,
    onCancel: () => { setV(lu); setErreur(null) },
    onSave: async () => {
      if ('erreur' in corps) return corps.erreur
      const e = await ecrivain.executer({
        ecran: 'soiree:modifier', corps: corps.corps, silencieux: true, succes: 'Soirée enregistrée.',
        envoi: (cle) => modifierSoiree(s.id, corps.corps, cle), preparationHorsLigne: !g.publier_requis,
      })
      if (!e) { setLu(v); return true }
      setErreur(e)
      return e.message
    },
  })

  const ro = !bouton.actif
  const verrou = (code: string) => g.verrous.includes(code)
  // Programmée, ou un billet vendu : la billetterie fige la date — la cause se dit telle qu'elle est.
  const nuitFigee = raisonNuitFigee(s)
  const nuitBloquee = nuitFigee !== null
  const lues = new Set(s.salles.map((x) => x.code))
  const set = <K extends keyof SaisieSoiree>(k: K, x: SaisieSoiree[K]) => setV({ ...v, [k]: x })

  return (
    <section className="bs-section" aria-labelledby="bs-soiree-titre">
      <h2 className="bs-h2" id="bs-soiree-titre">La soirée</h2>
      <div className="bs-form">
        {bouton.raison ? <p className="bs-note" role="status">{bouton.raison}</p> : null}
        {g.publier_requis && bouton.actif ? (
          <p className="bs-note bs-note--doute"><ShieldCheck size={14} aria-hidden="true" /> Cette soirée est en ligne : relever la jauge, élargir les horaires ou changer de nuit agit tout de suite sur la vente. Gestion vous demandera de confirmer votre identité.</p>
        ) : null}
        {g.avertissements.map((a) => <p key={a.code} className="bs-note bs-note--doute">{a.message}</p>)}
        {erreur?.genre === 'version' ? (
          <div className="bs-note bs-note--ko" role="alert">
            <span>{erreur.message}</span>
            <button type="button" className="lc-btn lc-btn--sm" onClick={() => { setLu(saisieDe(s, catalogue)); setV(saisieDe(s, catalogue)); setErreur(null); ecrivain.recharger() }}>
              <RefreshCw size={14} aria-hidden="true" /> Recharger
            </button>
          </div>
        ) : null}

        <div className="lc-field bs-champ bs-nom-soiree">
          <label className="lc-label" htmlFor="bs-s-nom">Nom de la soirée</label>
          <span className="bs-nom-soiree__champ">
            <input id="bs-s-nom" className="lc-input" value={v.nom} maxLength={120} disabled={ro} onChange={(e) => set('nom', e.target.value)} />
            <span className="bs-nom-soiree__compte" aria-hidden="true">{v.nom.length} / 120</span>
          </span>
        </div>

        <section className="bs-carte-reglage" aria-labelledby="bs-s-t-nuit">
          <div className="bs-carte-reglage__tete">
            <h3 className="bs-carte-reglage__titre" id="bs-s-t-nuit"><CalendarDays size={16} aria-hidden="true" /> La nuit et les horaires</h3>
            <p className="bs-carte-reglage__aide">Changer de nuit décale aussi les heures de vente des tarifs.</p>
          </div>
          <div className="bs-heures">
            <div className="lc-field">
              <label className="lc-label" htmlFor="bs-s-nuit">Nuit</label>
              <input id="bs-s-nuit" className="lc-input" type="date" value={v.nuit} disabled={ro || nuitBloquee} onChange={(e) => set('nuit', e.target.value)} />
              {nuitFigee ? <span className="lc-helper bs-champ__verrou">{nuitFigee}</span> : null}
            </div>
            <div className="lc-field">
              <label className="lc-label" htmlFor="bs-s-ouv">Ouverture</label>
              <input id="bs-s-ouv" className="lc-input" type="time" step={60} value={v.ouverture} disabled={ro} onChange={(e) => set('ouverture', e.target.value)} />
              {aideHeure(v.nuit, v.ouverture, verrou('HORAIRES_RESSERRER') ? 'On peut ouvrir plus tôt, pas plus tard.' : null)}
            </div>
            <div className="lc-field">
              <label className="lc-label" htmlFor="bs-s-ferm">Fermeture</label>
              <input id="bs-s-ferm" className="lc-input" type="time" step={60} value={v.fermeture} disabled={ro} onChange={(e) => set('fermeture', e.target.value)} />
              {aideHeure(v.nuit, v.fermeture, verrou('HORAIRES_RESSERRER') ? 'On peut fermer plus tard, pas plus tôt.' : null)}
            </div>
          </div>
          <p className="bs-carte-reglage__phrase">{phraseDeLaNuit(v)}</p>
        </section>

        <section className="bs-carte-reglage" aria-labelledby="bs-s-t-salles">
          <div className="bs-carte-reglage__tete">
            <h3 className="bs-carte-reglage__titre" id="bs-s-t-salles"><MapPin size={16} aria-hidden="true" /> Les salles et la jauge</h3>
            <p className="bs-carte-reglage__aide">{verrou('SALLES_RETRAIT') ? VERROU_LIBELLE['SALLES_RETRAIT'] : 'Touchez une salle pour l’ouvrir ou la fermer cette nuit. La jauge d’une salle est enregistrée, mais rien ne la contrôle : seule la jauge totale limite les ventes.'}</p>
          </div>
          <div className="bsw-salles bs-salles">
            {v.salles.map((x, i) => {
              const retraitBloque = x.retenue && lues.has(x.code) && verrou('SALLES_RETRAIT')
              const il = illustrationSalle(x.nom)
              return (
                <div key={x.code} className={`bsw-salle ${il.classe}${x.retenue ? ' bsw-salle--on' : ''}`}>
                  <button type="button" className="bsw-salle__bascule" aria-pressed={x.retenue} disabled={ro || retraitBloque}
                    title={retraitBloque ? VERROU_LIBELLE['SALLES_RETRAIT'] : undefined}
                    onClick={() => set('salles', v.salles.map((y, j) => (j === i ? { ...y, retenue: !y.retenue } : y)))}>
                    <span className="bsw-salle__illu">{il.svg}</span>
                    <span className="bsw-salle__nom">{x.nom}</span>
                    {il.style ? <span className="bsw-salle__style">{il.style}</span> : null}
                    <span className="bsw-salle__etat"><span className="bsw-pt" aria-hidden="true" />{x.retenue ? 'Ouverte' : 'Fermée'}</span>
                  </button>
                  {x.retenue ? (
                    <input className="lc-input bsw-salle__jauge" inputMode="numeric" placeholder="jauge (facultative)" aria-label={`Jauge de ${x.nom}`}
                      value={x.jauge} disabled={ro} onChange={(e) => set('salles', v.salles.map((y, j) => (j === i ? { ...y, jauge: e.target.value } : y)))} />
                  ) : null}
                </div>
              )
            })}
          </div>
          <div className="lc-field bs-jauge-totale">
            <label className="lc-label" htmlFor="bs-s-jauge">Jauge totale</label>
            <input id="bs-s-jauge" className="lc-input bs-court" inputMode="numeric" value={v.jauge_totale} disabled={ro} onChange={(e) => set('jauge_totale', e.target.value)} />
            <span className="lc-helper">
              C’est elle, et elle seule, qui limite les ventes à la porte.{typeof g.jauge_min === 'number' && g.jauge_min > 0 ? ` Pas moins que les ${g.jauge_min} place(s) déjà occupée(s).` : ''}
            </span>
          </div>
        </section>

        <section className="bs-carte-reglage" aria-labelledby="bs-s-t-pres">
          <div className="bs-carte-reglage__tete">
            <h3 className="bs-carte-reglage__titre" id="bs-s-t-pres"><AlignLeft size={16} aria-hidden="true" /> Présentation et famille</h3>
            <p className="bs-carte-reglage__aide">Facultatives.</p>
          </div>
          <div className="lc-field bs-famille">
            <label className="lc-label" htmlFor="bs-s-fam">Famille</label>
            <input id="bs-s-fam" className="lc-input" value={v.famille} maxLength={60} disabled={ro} onChange={(e) => set('famille', e.target.value)} />
          </div>
          <div className="lc-field">
            <label className="lc-label" htmlFor="bs-s-pres">Présentation</label>
            <textarea id="bs-s-pres" className="lc-input bs-zone" rows={4} maxLength={4000} value={v.presentation} disabled={ro} onChange={(e) => set('presentation', e.target.value)} />
          </div>
        </section>
      </div>
    </section>
  )
}
